import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Card,
  Chip,
  Stack,
  IconButton,
  useTheme,
  alpha,
  Divider,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  BarChart as BarChartIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Diamond as DiamondIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  Rocket as RocketIcon,
  Star as StarIcon,
  WorkspacePremium as PremiumIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import { showErrorToast } from "utils/toastNotification";
import { createUserSubscription } from "store/features/subscription/subscriptionActions";
import { selectSubscriptionLoading } from "store/features/subscription/subscriptionSelectors";
import api from "services/api";
import subscribeSvg from "assets/subscription/subscribe.svg";
import showPlansSvg from "assets/subscription/show-plans.svg";

const SubscriptionPlans = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subscribing = useSelector(selectSubscriptionLoading);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [darkmode, setDarkmode] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const isDark = savedTheme === "dark";
    setDarkmode(isDark);
  }, []);

  const toggleDarkmode = () => {
    const newMode = !darkmode;
    setDarkmode(newMode);
    localStorage.setItem("theme", newMode ? "dark" : "light");
    if (newMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get("v1/api/public/plans");
      console.log("API Response:", response);
      if (response?.meta?.status === true) {
        // Transform the data to include proper features array
        const transformedPlans = response.data.map((plan) => ({
          ...plan,
          features: plan.features || [],
          max_users: plan.max_users || 0,
          max_employees: plan.max_employees || 0,
          max_storage_mb: plan.max_storage_mb || 0,
        }));
        setPlans(transformedPlans);
      } else {
        showErrorToast("Failed to fetch plans");
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      showErrorToast("Error loading plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    const timer = setTimeout(() => setShowContent(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubscribe = async (planId) => {
    setSelectedPlan(planId);
    const result = await dispatch(createUserSubscription(planId));
    if (result.success) {
      setTimeout(() => {
        navigate("/company/dashboard");
      }, 2000);
    }
    setSelectedPlan(null);
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    if (numPrice === 0) return "Free";
    return `₹${numPrice.toLocaleString()}`;
  };

  const getPlanLevel = (planName) => {
    const name = planName?.toLowerCase() || "";
    if (name.includes("enterprise") || name.includes("ultimate"))
      return "enterprise";
    if (name.includes("pro") || name.includes("professional")) return "pro";
    if (name.includes("business")) return "business";
    if (name.includes("basic") || name.includes("starter")) return "basic";
    return "standard";
  };

  const getPlanGradient = (planName) => {
    const level = getPlanLevel(planName);
    switch (level) {
      case "enterprise":
        return "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
      case "pro":
        return "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)";
      case "business":
        return "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)";
      default:
        return "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)";
    }
  };

  const getFeatureIcon = (featureName) => {
    const featureStr =
      typeof featureName === "string" ? featureName.toLowerCase() : "";
    const icons = {
      auth: <SecurityIcon sx={{ fontSize: 18 }} />,
      tenant: <BusinessIcon sx={{ fontSize: 18 }} />,
      users: <PeopleIcon sx={{ fontSize: 18 }} />,
      employees: <PeopleIcon sx={{ fontSize: 18 }} />,
      attendance: <AccessTimeIcon sx={{ fontSize: 18 }} />,
      leave: <CalendarIcon sx={{ fontSize: 18 }} />,
      payroll: <CreditCardIcon sx={{ fontSize: 18 }} />,
      projects: <DescriptionIcon sx={{ fontSize: 18 }} />,
      reports: <BarChartIcon sx={{ fontSize: 18 }} />,
      notifications: <EmailIcon sx={{ fontSize: 18 }} />,
    };
    return icons[featureStr] || <CheckCircleIcon sx={{ fontSize: 18 }} />;
  };

  const formatFeatureName = (feature) => {
    const featureMap = {
      auth: "Authentication & Security",
      tenant: "Multi-Tenant Support",
      users: "User Management",
      employees: "Employee Management",
      attendance: "Attendance Tracking",
      leave: "Leave Management",
      payroll: "Payroll Processing",
      projects: "Project Management",
      reports: "Advanced Reports",
      notifications: "Email Notifications",
    };
    return featureMap[feature] || feature.replace(/_/g, " ");
  };

  const formatStorage = (mb) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(0)} GB`;
    return `${mb} MB`;
  };

  const isDarkMode = darkmode;

  if (loading && !showContent) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDarkMode
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #fefce8 100%)",
        }}
      >
        <CircularProgress
          size={48}
          sx={{
            color: "#f59e0b",
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: isDarkMode
          ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
          : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        transition: "all 0.3s ease",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* Floating orbs for decoration */}
      <Box
        sx={{
          position: "fixed",
          top: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(245,158,11,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Theme Toggle */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
        style={{ position: "fixed", right: 24, top: 24, zIndex: 50 }}
      >
        <IconButton
          onClick={toggleDarkmode}
          sx={{
            width: 44,
            height: 44,
            borderRadius: "14px",
            backgroundColor: isDarkMode
              ? alpha("#1e293b", 0.9)
              : alpha("#ffffff", 0.9),
            backdropFilter: "blur(12px)",
            border: `1px solid ${alpha("#f59e0b", 0.2)}`,
            boxShadow: `0 4px 12px ${alpha("#000", 0.1)}`,
            "&:hover": {
              backgroundColor: alpha("#f59e0b", 0.1),
              transform: "scale(1.05)",
            },
          }}
        >
          {isDarkMode ? (
            <LightModeIcon sx={{ color: "#f59e0b" }} />
          ) : (
            <DarkModeIcon />
          )}
        </IconButton>
      </motion.div>
      {/* Fixed Mirrored SVG - Bottom Right */}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 0,
          zIndex: 40,
          pointerEvents: "none",
        }}
      >
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
        >
          <Box
            component="img"
            src={showPlansSvg}
            alt=""
            sx={{
              width: { xs: 80, sm: 120, md: 150 },
              height: "auto",
              opacity: 0.9,
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.1))",
              transform: "scaleX(-1)",
              transition: "all 0.3s ease",
              "&:hover": {
                opacity: 0.8,
                transform: "scaleX(-1) scale(1.05)",
              },
            }}
          />
        </motion.div>
      </Box>
      {/* Welcome Animation */}
      <AnimatePresence>
        {!showContent && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            }}
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, type: "spring" }}
            >
              <Box
                component="img"
                src={subscribeSvg}
                alt="Subscribe"
                sx={{
                  width: { xs: 120, lg: 160 },
                  height: { xs: 120, lg: 160 },
                  filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.2))",
                }}
              />
            </motion.div>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
                  fontWeight: 800,
                  color: "#ffffff",
                  textAlign: "center",
                  mt: 4,
                }}
              >
                Choose Your Perfect Plan
              </Typography>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showContent ? 1 : 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <Container maxWidth="lg" sx={{ pt: { xs: 10, md: 12 }, pb: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{ textAlign: "center" }}
          >
            <Chip
              label="Pricing Plans"
              sx={{
                mb: 3,
                backgroundColor: alpha("#f59e0b", 0.1),
                color: "#f59e0b",
                fontWeight: 600,
                borderRadius: "20px",
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
                fontWeight: 800,
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 2,
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: "600px",
                mx: "auto",
                color: isDarkMode ? "#94a3b8" : "#64748b",
                fontSize: "1.1rem",
              }}
            >
              Choose the perfect plan for your business needs. All plans include
              a 14-day free trial.
            </Typography>
          </motion.div>
        </Container>

        {/* Plans Grid */}
        <Container maxWidth="xl" sx={{ pb: 12 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
              <CircularProgress sx={{ color: "#f59e0b" }} />
            </Box>
          ) : (
            <Grid container spacing={4} justifyContent="center">
              {plans.map((plan, index) => {
                const isPopular =
                  plan.name?.toLowerCase().includes("pro") ||
                  plan.name?.toLowerCase().includes("professional");
                const isHovered = hoveredPlan === plan.id;
                const planLevel = getPlanLevel(plan.name);

                return (
                  <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onHoverStart={() => setHoveredPlan(plan.id)}
                      onHoverEnd={() => setHoveredPlan(null)}
                    >
                      <Card
                        elevation={0}
                        sx={{
                          position: "relative",
                          height: "100%",
                          borderRadius: "32px",
                          overflow: "hidden",
                          background: isDarkMode
                            ? `linear-gradient(135deg, ${alpha(
                                "#1e293b",
                                0.95,
                              )} 0%, ${alpha("#0f172a", 0.95)} 100%)`
                            : `#ffffff`,
                          border: `1px solid ${alpha(
                            "#f59e0b",
                            isHovered ? 0.3 : 0.1,
                          )}`,
                          transform: isHovered
                            ? "translateY(-8px)"
                            : "translateY(0)",
                          transition: "all 0.3s ease",
                          boxShadow: isHovered
                            ? `0 20px 40px ${alpha("#000", 0.2)}`
                            : `0 8px 24px ${alpha("#000", 0.12)}`,
                        }}
                      >
                        {/* Gradient Top Bar */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: getPlanGradient(plan.name),
                          }}
                        />

                        {/* Popular Badge */}
                        {isPopular && (
                          <Chip
                            icon={
                              <StarIcon sx={{ fontSize: 14, color: "#fff" }} />
                            }
                            label="Most Popular"
                            sx={{
                              position: "absolute",
                              top: 16,
                              right: 16,
                              backgroundColor: "#f59e0b",
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: 28,
                              zIndex: 1,
                            }}
                          />
                        )}

                        {/* Plan Icon */}
                        <Box sx={{ p: 3, textAlign: "center", pt: 4 }}>
                          <motion.div
                            animate={{
                              rotate: isHovered ? [0, -5, 5, 0] : 0,
                              scale: isHovered ? 1.1 : 1,
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            <Box
                              sx={{
                                width: 70,
                                height: 70,
                                mx: "auto",
                                borderRadius: "24px",
                                background: alpha("#f59e0b", 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                              }}
                            >
                              {planLevel === "enterprise" ? (
                                <PremiumIcon
                                  sx={{ fontSize: 36, color: "#f59e0b" }}
                                />
                              ) : planLevel === "pro" ? (
                                <RocketIcon
                                  sx={{ fontSize: 36, color: "#f59e0b" }}
                                />
                              ) : (
                                <SpeedIcon
                                  sx={{ fontSize: 36, color: "#f59e0b" }}
                                />
                              )}
                            </Box>
                          </motion.div>

                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              color: isDarkMode ? "#fff" : "#1e293b",
                              mb: 1,
                            }}
                          >
                            {plan.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? "#94a3b8" : "#64748b",
                              mb: 2,
                            }}
                          >
                            {plan.description ||
                              "Perfect for your business needs"}
                          </Typography>

                          <Stack
                            direction="row"
                            alignItems="baseline"
                            justifyContent="center"
                            spacing={0.5}
                          >
                            <Typography
                              variant="h2"
                              sx={{
                                fontWeight: 800,
                                fontSize: "2.5rem",
                                color: isDarkMode ? "#fff" : "#1e293b",
                              }}
                            >
                              {formatPrice(plan.price)}
                            </Typography>
                            {plan.price !== "0" && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isDarkMode ? "#94a3b8" : "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                /
                                {plan.billing_cycle === "monthly"
                                  ? "month"
                                  : "year"}
                              </Typography>
                            )}
                          </Stack>

                          {plan.trial_days > 0 && plan.price !== "0" && (
                            <Chip
                              label={`${plan.trial_days} days free trial`}
                              size="small"
                              sx={{
                                mt: 1,
                                backgroundColor: alpha("#10b981", 0.1),
                                color: "#10b981",
                                fontSize: "0.7rem",
                              }}
                            />
                          )}
                        </Box>

                        <Divider
                          sx={{ mx: 3, borderColor: alpha("#f59e0b", 0.1) }}
                        />

                        {/* Limits Section */}
                        <Box sx={{ p: 3 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              letterSpacing: "0.05em",
                              color: "#f59e0b",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                            }}
                          >
                            Plan Limits
                          </Typography>
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={4}>
                              <Stack alignItems="center" spacing={0.5}>
                                <PeopleIcon
                                  sx={{
                                    fontSize: 20,
                                    opacity: 0.7,
                                    color: isDarkMode ? "#ffffff" : "#1e293b",
                                  }}
                                />
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    color: isDarkMode ? "#ffffff" : "#1e293b",
                                  }}
                                >
                                  {plan.max_users || "Unlimited"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#64748b" }}
                                >
                                  Users
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={4}>
                              <Stack alignItems="center" spacing={0.5}>
                                <BusinessIcon
                                  sx={{
                                    fontSize: 20,
                                    color: "#f59e0b",
                                    opacity: 0.7,
                                  }}
                                />
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    color: isDarkMode ? "#ffffff" : "#1e293b",
                                  }}
                                >
                                  {plan.max_employees || "Unlimited"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#64748b" }}
                                >
                                  Employees
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={4}>
                              <Stack alignItems="center" spacing={0.5}>
                                <StorageIcon
                                  sx={{
                                    fontSize: 20,
                                    color: "#f59e0b",
                                    opacity: 0.7,
                                  }}
                                />
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    color: isDarkMode ? "#ffffff" : "#1e293b",
                                  }}
                                >
                                  {formatStorage(plan.max_storage_mb || 5120)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#64748b" }}
                                >
                                  Storage
                                </Typography>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>

                        <Divider
                          sx={{ mx: 3, borderColor: alpha("#f59e0b", 0.1) }}
                        />

                        {/* Features Section */}
                        <Box sx={{ p: 3 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              letterSpacing: "0.05em",
                              color: "#f59e0b",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                            }}
                          >
                            Features Included
                          </Typography>
                          <Stack spacing={1.5} sx={{ mt: 2 }}>
                            {plan.features?.map((feature, idx) => (
                              <Stack
                                key={idx}
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                              >
                                <Box sx={{ color: "#10b981" }}>
                                  {getFeatureIcon(feature)}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isDarkMode ? "#cbd5e1" : "#475569",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {formatFeatureName(feature)}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>

                        {/* Subscribe Button */}
                        <Box sx={{ p: 3, pt: 0 }}>
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => handleSubscribe(plan.id)}
                              disabled={subscribing && selectedPlan === plan.id}
                              sx={{
                                py: 1.5,
                                borderRadius: "20px",
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                textTransform: "none",
                                background: getPlanGradient(plan.name),
                                color: "#fff",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: `0 8px 20px ${alpha(
                                    "#f59e0b",
                                    0.4,
                                  )}`,
                                },
                              }}
                            >
                              {subscribing && selectedPlan === plan.id ? (
                                <CircularProgress
                                  size={20}
                                  sx={{ color: "#fff" }}
                                />
                              ) : plan.price === "0" ? (
                                "Start Free Trial"
                              ) : (
                                "Subscribe Now"
                              )}
                            </Button>
                          </motion.div>
                        </Box>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </motion.div>
    </Box>
  );
};

export default SubscriptionPlans;
